import React from 'react';
import { ArrowLeft } from 'lucide-react';

/**
 * The way back out of a detail on a phone.
 *
 * Every master-detail pane in the wizard — outreach threads, BOM checks,
 * supplier discovery — becomes two screens on a phone rather than two columns
 * or, worse, two stacked boxes with no visible relationship. This is the row
 * that sits on top of the second screen and returns to the first, and it is
 * one component so the three of them cannot drift into three different ways of
 * saying "back".
 *
 * It says only that. What was opened is named by the detail's own header
 * underneath, and a back row that repeats it makes the screen say the same
 * thing twice before any content starts.
 */
export interface BackToListRowProps {
  /** What you are going back to, e.g. "All threads". */
  label: string;
  /** How many are waiting there; omit when the number is not useful. */
  count?: number;
  onBack: () => void;
}

export const BackToListRow: React.FC<BackToListRowProps> = ({ label, count, onBack }) => (
  <button
    type="button"
    onClick={onBack}
    className="flex w-full shrink-0 items-center gap-s3 border-b border-line-divider bg-canvas px-s5 py-s3 text-left focus:outline-none focus-visible:bg-surface-hover"
  >
    <ArrowLeft className="h-4 w-4 shrink-0 text-ink-secondary" />
    <span className="text-body-sm font-semibold text-ink">{label}</span>
    {count !== undefined && <span className="text-meta text-ink-muted">· {count}</span>}
  </button>
);

export default BackToListRow;
